import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReportStatus } from "enums";

// allowance for the difference between the client and server clocks when matching a report to its request
const CLOCK_SKEW_TOLERANCE = 5 * 60 * 1000;

const readList = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const useStoredList = (key) => {
  const [list, setList] = useState(() => readList(key));
  const current = useRef(list);

  useEffect(() => {
    current.current = readList(key);
    setList(current.current);
  }, [key]);

  const update = useCallback(
    (next) => {
      current.current = next;
      localStorage.setItem(key, JSON.stringify(next));
      setList(next);
    },
    [key],
  );

  return [list, current, update];
};

const removeAt = (list, index) => list.filter((_, i) => i !== index);

// Tracks exports per user, across stations: "pending" ones are requested but not finished yet,
// "newDownloads" holds the report IDs of successful exports the user has not acknowledged
const useNewDownloads = ({ userId }) => {
  const [newDownloads, newRef, setNew] = useStoredList(`newReports:${userId}`);
  const [pendingDownloads, pendingRef, setPending] = useStoredList(`pendingDownloads:${userId}`);

  const addPendingDownload = useCallback(
    (fileName) => {
      if (!fileName) return;
      setPending([...pendingRef.current, { fileName, requestedAt: Date.now() }]);
    },
    [pendingRef, setPending],
  );

  const completeDownload = useCallback(
    (fileName, reportId) => {
      const index = pendingRef.current.findIndex((item) => item.fileName === fileName);
      if (index === -1) return;
      setPending(removeAt(pendingRef.current, index));
      const id = String(reportId ?? "");
      if (id && !newRef.current.includes(id)) setNew([...newRef.current, id]);
    },
    [newRef, pendingRef, setNew, setPending],
  );

  const discardPendingDownload = useCallback(
    (fileName) => {
      const index = pendingRef.current.findIndex((item) => item.fileName === fileName);
      if (index === -1) return;
      setPending(removeAt(pendingRef.current, index));
    },
    [pendingRef, setPending],
  );

  const acknowledgeDownload = useCallback(
    (reportId) => {
      const index = newRef.current.indexOf(String(reportId));
      if (index === -1) return;
      setNew(removeAt(newRef.current, index));
    },
    [newRef, setNew],
  );

  // Resolves pending exports from the reports loaded in the Downloads page
  const syncDownloads = useCallback(
    (reports) => {
      if (!pendingRef.current.length) return;
      const used = new Set();
      const completed = [];
      const remaining = [];

      pendingRef.current.forEach((item) => {
        const report = reports.find(
          (r) =>
            r.fileName === item.fileName &&
            !used.has(r.reportId) &&
            new Date(r.createdAt).getTime() >= item.requestedAt - CLOCK_SKEW_TOLERANCE,
        );
        if (report) used.add(report.reportId);

        if (!report || report.status === ReportStatus.InProgress) {
          remaining.push(item);
        } else if (report.status === ReportStatus.Success) {
          completed.push(String(report.reportId));
        }
      });

      if (remaining.length === pendingRef.current.length) return;
      setPending(remaining);
      if (completed.length) setNew([...newRef.current, ...completed]);
    },
    [newRef, pendingRef, setNew, setPending],
  );

  return useMemo(
    () => ({
      newDownloads,
      pendingCount: pendingDownloads.length,
      addPendingDownload,
      completeDownload,
      discardPendingDownload,
      acknowledgeDownload,
      syncDownloads,
    }),
    [
      newDownloads,
      pendingDownloads.length,
      addPendingDownload,
      completeDownload,
      discardPendingDownload,
      acknowledgeDownload,
      syncDownloads,
    ],
  );
};

export default useNewDownloads;
